// Keyword Tracking & Rankings Component
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, TrendingUp, TrendingDown, Minus, RefreshCw, Target, Search, BarChart3 } from 'lucide-react';

export function KeywordTracker() {
  const [loading, setLoading] = useState(true);
  const [keywords, setKeywords] = useState([]);
  const [stats, setStats] = useState(null);
  const [topKeywords, setTopKeywords] = useState([]);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [keywordsData, statsData, topData] = await Promise.all([
        api.request('/keywords/tracking/keywords'),
        api.request('/keywords/tracking/stats'),
        api.request('/keywords/tracking/top-keywords?limit=10')
      ]);
      
      setKeywords(keywordsData.keywords || []);
      setStats(statsData);
      setTopKeywords(topData.keywords || []);
    } catch (err) {
      console.error('Failed to load keyword data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCheckRankings() {
    if (!confirm('Check rankings for all keywords? This may take a few minutes.')) return;
    
    setChecking(true);
    try {
      await api.request('/keywords/tracking/check-rankings', { method: 'POST' });
      alert('Rankings updated successfully!');
      await loadData();
    } catch (err) {
      alert('Failed to check rankings: ' + err.message);
    } finally {
      setChecking(false);
    }
  }

  function getTrendIcon(trend) {
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend < 0) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  }

  function getTrendBadge(trend) {
    if (trend > 0) return <Badge className="bg-green-100 text-green-800">+{trend}</Badge>;
    if (trend < 0) return <Badge className="bg-red-100 text-red-800">{trend}</Badge>;
    return <Badge variant="outline">—</Badge>;
  }

  function getRankBadge(rank) {
    if (!rank) return <Badge variant="outline">Not Ranked</Badge>;
    if (rank <= 3) return <Badge className="bg-green-600 text-white">#{rank}</Badge>;
    if (rank <= 10) return <Badge className="bg-blue-600 text-white">#{rank}</Badge>;
    if (rank <= 30) return <Badge className="bg-yellow-600 text-white">#{rank}</Badge>;
    return <Badge variant="outline">#{rank}</Badge>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <Target className="w-4 h-4 mr-2" />
              Total Keywords
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_tracked || 0}</div>
            <div className="text-xs text-gray-500 mt-1">Being tracked</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <TrendingUp className="w-4 h-4 mr-2" />
              Top 10 Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats?.top_10_rankings || 0}</div>
            <div className="text-xs text-gray-500 mt-1">First page results</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <BarChart3 className="w-4 h-4 mr-2" />
              Average Rank
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.average_rank || '—'}</div>
            <div className="text-xs text-gray-500 mt-1">Across all keywords</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
              Improving
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats?.improved_rankings || 0}</div>
            <div className="text-xs text-gray-500 mt-1">Rankings up</div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Keyword Rankings</h2>
        <Button 
          onClick={handleCheckRankings} 
          disabled={checking}
          variant="outline"
        >
          {checking ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Check Rankings
            </>
          )}
        </Button>
      </div>

      {/* Top Performing Keywords */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
            Top Performing Keywords
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topKeywords.map((kw, index) => (
              <div 
                key={kw.id} 
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <div className="text-2xl font-bold text-gray-400">#{index + 1}</div>
                  <div className="flex-1">
                    <div className="font-medium">{kw.keyword}</div>
                    <div className="flex items-center space-x-3 text-sm text-gray-500">
                      {kw.search_volume && (
                        <span className="flex items-center">
                          <Search className="w-3 h-3 mr-1" />
                          {kw.search_volume.toLocaleString()} searches/mo
                        </span>
                      )}
                      {kw.category && (
                        <Badge variant="outline">{kw.category}</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">Google</div>
                    {getRankBadge(kw.google_rank)}
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">Bing</div>
                    {getRankBadge(kw.bing_rank)}
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">Trend</div>
                    <div className="flex items-center space-x-1">
                      {getTrendIcon(kw.trend || 0)}
                      {getTrendBadge(kw.trend || 0)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* All Keywords Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Tracked Keywords ({keywords.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2">Keyword</th>
                  <th className="text-left py-3 px-2">Category</th>
                  <th className="text-right py-3 px-2">Volume</th>
                  <th className="text-center py-3 px-2">Google</th>
                  <th className="text-center py-3 px-2">Bing</th>
                  <th className="text-center py-3 px-2">Trend</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((kw) => (
                  <tr key={kw.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2">
                      <div className="font-medium">{kw.keyword}</div>
                      {kw.relevance_score && (
                        <div className="text-xs text-gray-500">
                          Relevance: {kw.relevance_score}%
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2">
                      {kw.category && (
                        <Badge variant="outline" className="text-xs">
                          {kw.category}
                        </Badge>
                      )}
                    </td>
                    <td className="text-right py-3 px-2">
                      {kw.search_volume ? kw.search_volume.toLocaleString() : '—'}
                    </td>
                    <td className="text-center py-3 px-2">
                      {getRankBadge(kw.google_rank)}
                    </td>
                    <td className="text-center py-3 px-2">
                      {getRankBadge(kw.bing_rank)}
                    </td>
                    <td className="text-center py-3 px-2">
                      <div className="flex items-center justify-center space-x-1">
                        {getTrendIcon(kw.google_trend || 0)}
                        {getTrendBadge(kw.google_trend || 0)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2">Ranking Badges:</h4>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Badge className="bg-green-600 text-white">#1-3</Badge>
                  <span>Featured snippet territory</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className="bg-blue-600 text-white">#4-10</Badge>
                  <span>First page (top 10)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className="bg-yellow-600 text-white">#11-30</Badge>
                  <span>First 3 pages</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Trend Indicators:</h4>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span>Ranking improved (moved up)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <span>Ranking declined (moved down)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Minus className="w-4 h-4 text-gray-400" />
                  <span>No change</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-blue-300">
            <p className="text-xs text-gray-700">
              <strong>Note:</strong> Rankings are checked automatically daily. Keywords are discovered weekly using AI to find relevant, high-volume search terms for UPost.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
